<template>
  <div>
    <section class="section">
      <div>
        <div class="columns">
          <div class="column is-narrow">
            <div class="title is-4">
              <strong>
                Familles de produits
              </strong>
            </div>
            <b-tabs
              v-if="types"
              v-model="activeTab"
              vertical
            >
              <b-tab-item
                v-for="type in types"
                :key="type"
              >
                <template
                  slot="header"
                >
                  <div
                    class="border-l-4 mb-2 w-full font-bold"
                    :class="classesFor(type, type === filters.type)"
                  >
                    {{ nameFor(type) }}
                  </div>
                </template>
              </b-tab-item>
            </b-tabs>
          </div>
          <div class="column custom-height">
            <div class="flex flex-col h-full">
              <div class="title is-4 has-text-primary uppercase mb-8">
                1/3 - Configuration de la baie de brassage
              </div>
              <ProductFilters />


              <b-loading
                :active="!productsFetched"
              />

              <DynamicScroller
                ref="scroller"
                :items="displayedProducts"
                :min-item-size="54"
                class="w-full flex-1 box overflow-y-auto bg-gray"
              >
                <template v-slot="{ item: product, index, active }">
                  <DynamicScrollerItem
                    class="columns mx-0 px-4 py-4"
                    :item="product"
                    :active="active"
                    :size-dependencies="[
                      productTextAttributes(product).length,
                      !!product.photo_url,
                    ]"
                    :data-index="index"
                  >
                    <BaseCard
                      :card-title="product.designation"
                      :text-lines="productTextAttributes(product)"
                      :image-url="product.photo_url"
                      :small-title="productQuantityText(product)"
                      class="w-full mb-2"
                      :class="{ border: productQuantityText(product) }"
                    >
                      <div class="flex items-end items-center">
                        <b-button
                          type="is-danger"
                          icon-left="trash"
                          class="mr-2"
                          outlined
                          :disabled="!getQuantityByProduct(product)"
                          @click="resetProductQuantity(product)"
                        />
                        <p class="has-text-weight-bold mr-2">
                          Qté :
                        </p>
                        <form
                          @submit.prevent="
                            setProductQuantity({ product, quantity: product.quantity })"
                        >
                          <b-field
                            class="w-40 items-center"
                          >
                            <b-input
                              v-model="product.quantity"
                              type="number"
                              min="0"
                              step="1"
                            />
                            <p class="control">
                              <b-button
                                v-if="!getQuantityByProduct(product)"
                                type="is-primary"
                                native-type="submit"
                              >
                                Ajouter
                              </b-button>
                              <b-button
                                v-else
                                type="is-primary"
                                native-type="submit"
                              >
                                Modifier
                              </b-button>
                            </p>
                          </b-field>
                        </form>
                      </div>
                    </BaseCard>
                  </DynamicScrollerItem>
                </template>
              </DynamicScroller>
            </div>
          </div>

          <div class="pl-8 column is-one-quarter mx-auto text-center">
            <BaseNotice
              v-if="warningNotice.display"
              icon-class="has-text-info"
            >
              <p class="has-text-info">
                {{ warningNotice.message }}
              </p>
            </BaseNotice>
            <p v-else>
              Nombre d'unités occupées : {{ configurationSize }}
            </p>
            <BaseNotice
              v-if="containers.length > 1"
              icon-class="has-text-info"
            >
              <p class="has-text-info">
                Nous ne pouvons pas afficher d'aperçu pour cette configuration
              </p>
            </BaseNotice>
            <div class="custom-height w-full">
              <TheBaie
                v-if="!containers.length || containers.length === 1"
                :elements="baieElements"
                :container="container"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
    <b-navbar
      fixed-bottom
      shadow
      class="pl-4 pr-4 pt-4"
    >
      <template slot="end">
        <b-button
          size="is-medium"
          class="is-success mr-4 button-bold"
          tag="router-link"
          :to="containerChoiceLink"
        >
          J'ai terminé, je choisis ma baie
        </b-button>
      </template>
    </b-navbar>
  </div>
</template>

<script>
import { indexOf, isEmpty } from 'lodash';
import BaseCard from '@/components/BaseCard.vue';
import TheBaie from '@/components/TheBaie.vue';
import BaseNotice from '@/components/BaseNotice.vue';
import { mapGetters, mapState } from 'vuex';
import {
  ACCESSOIRE,
  PANNEAU_BRASSAGE,
  EMBASE_RJ45,
  BANDEAU_PRISE_ELECTRIQUE,
  CORDON_RJ45,
  GESTION_CABLE,
  JARRETIERE,
  ONDULEUR,
  PLATEAU,
  TIROIR_OPTIQUE,
  SWITCH,
  NAMES_FOR_TYPE,
  COLOR_FOR_TYPE,
} from '../constants';
import configurationMixin from './mixins/configurationMixin';
import ProductFilters from './partials/ProductFilters.vue';

export default {
  name: 'Configuration',
  components: {
    BaseCard, ProductFilters, TheBaie, BaseNotice,
  },
  mixins: [configurationMixin],
  data() {
    return {
      types: [
        PANNEAU_BRASSAGE,
        EMBASE_RJ45,
        BANDEAU_PRISE_ELECTRIQUE,
        GESTION_CABLE,
        PLATEAU,
        ACCESSOIRE,
        TIROIR_OPTIQUE,
        SWITCH,
        ONDULEUR,
        CORDON_RJ45,
        JARRETIERE,
      ],
    };
  },
  computed: {
    activeTab: {
      get() {
        return indexOf(this.types, this.filters.type);
      },
      set(index) {
        this.selectType(this.types[index]);
        this.$nextTick(() => {
          this.$refs.scroller.$el.scrollTop = 0;
        });
      },
    },
    ...mapState('configuration', ['productsFetched']),
    displayedProducts() {
      if (this.sortedProducts.length) {
        return this.sortedProducts;
      }
      return this.filteredProducts;
    },
    containerChoiceLink() {
      if (!isEmpty(this.containers)) {
        return this.getConfigurationPath('/choix-baie');
      }
      return this.getConfigurationPath('/suggestion-baie');
    },
    ...mapGetters('configuration', ['configurationSize', 'containers', 'container']),
    warningNotice() {
      const warningMessage = 'Attention, votre configuration va dépasser la taille maximale pour';
      const maxMessage = 'Attention, votre configuration a atteint la capacité maximale pour';
      let message;

      if (this.configurationSize >= 20 && this.configurationSize < 22) {
        message = `${warningMessage} un coffret (${this.configurationSize}U/22U)`;
      } else if (this.configurationSize === 22) {
        message = `${maxMessage} un coffret : 22U`;
      } else if (this.configurationSize >= 37 && this.configurationSize < 42) {
        message += `${warningMessage} une baie (${this.configurationSize}/42)`;
      } else if (this.configurationSize === 42) {
        message = `${maxMessage} une baie : 42U`;
      }

      return {
        display: !!message,
        message,
      };
    },
  },
  mounted() {
    this.selectType(this.types[0]);
  },
  methods: {
    classesFor(type, active) {
      const color = COLOR_FOR_TYPE[type];
      if (active) {
        return [`bg-${color}`, 'text-white'];
      }
      return `border-${color} hover:bg-${color} hover:text-white`;
    },
    nameFor(type) {
      return NAMES_FOR_TYPE[type];
    },
  },
};
</script>

<style>
.custom-height {
  height: 85vh;
}

.with-min-height {
  min-height: 120px;
}
</style>
